// @flow

import type {
  MarkdocTagRegistration,
  MarkdocFragmentRegistration,
} from 'docs/markdoc';
import {
  Tag,
  ContentGroupItem,
  getLanguageTitle,
  isMarkdocAstTag,
} from 'docs/util';
import {CodeBlock} from '../CodeBlock/CodeBlock.markdoc';

const getLangFromBeforeAfterTable = (beforeAfterTable): string => {
  if (!isMarkdocAstTag(beforeAfterTable)) {
    return '';
  }

  const beforeNodes = beforeAfterTable.attributes.beforeNodes;
  const afterNodes = beforeAfterTable.attributes.afterNodes;

  let lang = null;

  if (beforeNodes[0].name === CodeBlock.component) {
    lang = beforeNodes[0].attributes.language;
  }
  if (afterNodes[0].name === CodeBlock.component) {
    lang = afterNodes[0].attributes.language;
  }

  if (lang == null) {
    throw new Error(
      'The before-after-table tag could not determine the language. Please specify with the langs attributes on the before-after-table-group.',
    );
  }

  return lang;
};

const turnOffCodeLineNumbers = (children) => {
  // Code Blocks have line numbers on by default - turn those off explictly
  // inside before/after tables unless overridden.
  children.forEach((child) => {
    if (!isMarkdocAstTag(child)) {
      return;
    }

    if (child.name === CodeBlock.component) {
      child.attributes.numbered = false;
    }
  });
};

export const BeforeAfterTableGroup: MarkdocTagRegistration = {
  tag: 'before-after-table-group',
  component: 'BeforeAfterTableGroup',
  children: ['before-after-table'],
  attributes: {
    langs: {
      type: Array,
      required: false,
      description:
        'Pass in an array of languages to set the tab titles of the before after group. If not passed in, it tries to determine them from the languages in the code blocks inside of them.',
    },
  },
  render(node, config) {
    const children = node.renderChildren(config);
    const attributes = node.renderAttributes(this.attributes, config);
    const langs: ?Array<string> = (attributes.langs: any);

    // The TabGroup component expects a list of ContentGroupItems which it uses as tabs.
    const contentGroupItems = children.map((childNode, index) => {
      const lang = langs
        ? langs[index]
        : getLangFromBeforeAfterTable(childNode);
      const title = getLanguageTitle(lang);
      return new ContentGroupItem(lang, title, childNode);
    });

    const pref =
      langs && langs.includes('javascript_esnext') ? 'lang-js' : 'lang';

    return new Tag(this.tag, {
      pref,
      items: contentGroupItems,
    });
  },
};

export const BeforeAfterTable: MarkdocTagRegistration = {
  tag: 'before-after-table',
  component: 'BeforeAfterTable',
  attributes: {
    numbered: {
      type: Boolean,
      default: false,
      description:
        'Pass true to turn line numbers on. Line numbers are off by default.',
    },
  },
  validate(node) {
    if (node.children.length !== 2) {
      return [
        {
          id: 'before-after-table-children',
          level: 'critical',
          message: 'Before After Tables must only have a before and after tag.',
        },
      ];
    }
    return [];
  },
  render(node, config) {
    const beforeNodes = node.children[0].renderChildren(config);
    const afterNodes = node.children[1].renderChildren(config);
    const attributes = node.renderAttributes(this.attributes, config);

    if (!attributes.numbered) {
      turnOffCodeLineNumbers(beforeNodes);
      turnOffCodeLineNumbers(afterNodes);
    }

    return new Tag(this.tag, {beforeNodes, afterNodes});
  },
};

export const Before: MarkdocFragmentRegistration = {
  tag: 'before',
  attributes: {},
};

export const After: MarkdocFragmentRegistration = {
  tag: 'after',
  attributes: {},
};
